// ajay:code all 27-08-26
import { Trash2, Plus } from "lucide-react";
import MethodRow from "./MethodRow";

const textInput = {
  width: "100%",
  padding: "5px 8px",
  fontSize: 12,
  borderRadius: 6,
  border: "1px solid var(--border2)",
  background: "var(--surface2)",
  color: "var(--text)",
};

const fieldLabel = {
  fontSize: 10,
  color: "var(--text3)",
  fontWeight: 700,
  marginBottom: 3,
};

const addBtnStyle = {
  display: "flex",
  alignItems: "center",
  gap: 4,
  background: "none",
  border: "1px dashed var(--border2)",
  borderRadius: 6,
  padding: "5px 10px",
  fontSize: 11,
  fontWeight: 600,
  color: "var(--accent)",
  cursor: "pointer",
  marginTop: 8,
};

const emptyMethod = () => ({
  evaluation_technique: "",
  sample_size: "",
  sample_frequency: "",
  error_proofing: "",
  controlled_by_worker: false,
  controlled_by_inspector: false,
});

function Field({
  label,
  value,
  onChange,
  readOnly,
  textarea,
  rows = 2,
  placeholder,
}) {
  return (
    <div>
      <div style={fieldLabel}>{label}</div>
      {readOnly ? (
        <div style={{ fontSize: 12, whiteSpace: "pre-wrap" }}>
          {value || "—"}
        </div>
      ) : textarea ? (
        <textarea
          rows={rows}
          value={value || ""}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          style={textInput}
        />
      ) : (
        <input
          type="text"
          value={value || ""}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          style={textInput}
        />
      )}
    </div>
  );
}

export default function CharacteristicRow({
  characteristic: c,
  onChange,
  onDelete,
  readOnly,
}) {
  const set = (field, value) => onChange({ ...c, [field]: value });

  const methods = c.methods || [];
  const updateMethod = (i, updated) => {
    const next = [...methods];
    next[i] = updated;
    set("methods", next);
  };
  const removeMethod = (i) =>
    set(
      "methods",
      methods.filter((_, idx) => idx !== i),
    );
  const addMethod = () => set("methods", [...methods, emptyMethod()]);

  return (
    <div
      style={{
        background: "var(--surface)",
        border: "1px solid var(--border)",
        borderRadius: 8,
        padding: 10,
        marginBottom: 8,
      }}
    >
      <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
        <div style={{ width: 70 }}>
          <Field
            label="No."
            value={c.char_no}
            onChange={(v) => set("char_no", v)}
            readOnly={readOnly}
          />
        </div>
        <div style={{ flex: 1 }}>
          <Field
            label="Special Char Class"
            value={c.special_char_class}
            onChange={(v) => set("special_char_class", v)}
            readOnly={readOnly}
            placeholder="e.g. SC, ----"
          />
        </div>
        {!readOnly && (
          <button
            type="button"
            title="Delete characteristic"
            onClick={onDelete}
            style={{
              background: "none",
              border: "none",
              color: "var(--danger)",
              cursor: "pointer",
              marginTop: 16,
            }}
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 10,
          marginTop: 8,
        }}
      >
        <Field
          label="Product Characteristic"
          value={c.product_characteristic}
          onChange={(v) => set("product_characteristic", v)}
          readOnly={readOnly}
        />
        <Field
          label="Process Characteristic"
          value={c.process_characteristic}
          onChange={(v) => set("process_characteristic", v)}
          readOnly={readOnly}
        />
      </div>

      <div style={{ marginTop: 8 }}>
        <Field
          label="Specification / Tolerance (Product / Process)"
          value={c.specification_tolerance}
          onChange={(v) => set("specification_tolerance", v)}
          readOnly={readOnly}
          textarea
          rows={3}
        />
      </div>

      <div style={{ marginTop: 10 }}>
        <div style={{ ...fieldLabel, marginBottom: 4 }}>
          Methods ({methods.length})
        </div>
        {methods.map((m, i) => (
          <MethodRow
            key={i}
            method={m}
            index={i}
            onChange={(updated) => updateMethod(i, updated)}
            onDelete={() => removeMethod(i)}
            readOnly={readOnly}
          />
        ))}
        {methods.length === 0 && (
          <div style={{ fontSize: 11, color: "var(--text3)" }}>
            {readOnly ? "No methods" : "No methods yet"}
          </div>
        )}
        {!readOnly && (
          <button type="button" onClick={addMethod} style={addBtnStyle}>
            <Plus size={12} /> Add Method
          </button>
        )}
      </div>
    </div>
  );
}
